import * as React from 'react';

type UseCopyToClipboardOptions = {
  /** Duration (ms) the `copied` flag stays true after a successful copy. */
  resetDelay?: number;
  onError?: (error: unknown) => void;
};

type UseCopyToClipboardResult = {
  copied: boolean;
  copy: (text: string) => void;
};

export const useCopyToClipboard = ({
  resetDelay = 1500,
  onError,
}: UseCopyToClipboardOptions = {}): UseCopyToClipboardResult => {
  const [copied, setCopied] = React.useState(false);
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  // Hold the latest `onError` in a ref so the returned `copy` stays stable
  // across renders even when consumers pass inline callbacks.
  const onErrorRef = React.useRef(onError);
  React.useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const copy = React.useCallback(
    (text: string) => {
      // Some embedding contexts (sandboxed iframes, insecure contexts) ship
      // without the async clipboard API. Bail out cleanly instead of throwing.
      if (!navigator.clipboard?.writeText) {
        onErrorRef.current?.(new Error('Clipboard API unavailable'));
        return;
      }
      navigator.clipboard
        .writeText(text)
        .then(() => {
          setCopied(true);
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
          }
          timeoutRef.current = setTimeout(() => setCopied(false), resetDelay);
        })
        .catch((error) => {
          onErrorRef.current?.(error);
        });
    },
    [resetDelay],
  );

  return { copied, copy };
};
